/**
 * One Analytics board, opened by anyone it was shared with.
 *
 * The Dashboards library itself lives under /company-metrics, which is
 * admin-only — so without this page a board shared with a rep, or pinned to
 * their home screen by the Dashboard widget, would land them on the
 * "unauthorized" screen. Here they get the board and nothing else: the
 * builder renders read-only, so there is no toolbar that would 403 on use.
 *
 * The server is the authority on who may look. A board this person can't
 * read comes back 403/404 and they see the not-shared notice instead.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "../CompanyMetrics/dashboards/dashboards.css";
import { GET_DASHBOARD } from "../CompanyMetrics/dashboards/dashboardStore";
import DashboardBuilder from "../CompanyMetrics/dashboards/DashboardBuilder";
import useDashboardMetrics from "../CompanyMetrics/dashboards/useDashboardMetrics";
import PageHeader from "../../components/PageHeader";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import { UseCompanyColors } from "../../hooks/company";
import { DEFAULT_BRAND } from "../../helpers/brandColor";

const Notice = ({ title, body }) => (
  <SoftBox
    sx={{
      margin: { mini: "1rem", sm: "1.5rem" },
      padding: { mini: "2rem 1.25rem", sm: "3rem 2rem" },
      background: "#fff",
      border: "1px solid #EAECF0",
      borderRadius: "16px",
      textAlign: "center",
    }}
  >
    <SoftTypography sx={{ fontSize: "18px", fontWeight: 600, color: "#0F172A", display: "block" }}>
      {title}
    </SoftTypography>
    <SoftTypography
      sx={{ fontSize: "14px", color: "#667085", display: "block", marginTop: "6px" }}
    >
      {body}
    </SoftTypography>
  </SoftBox>
);

const SharedDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const metricsApi = useDashboardMetrics();
  const { primaryColor } = UseCompanyColors();

  const [board, setBoard] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | denied | error

  useEffect(() => {
    let alive = true;
    setState("loading");
    GET_DASHBOARD(id)
      .then((res) => {
        if (!alive) return;
        const data = res?.data;
        if (!data) {
          setState("denied");
          return;
        }
        setBoard(data);
        setState("ready");
      })
      .catch((err) => {
        if (!alive) return;
        const code = err?.response?.status;
        setState(code === 403 || code === 404 ? "denied" : "error");
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const brand = primaryColor || DEFAULT_BRAND;

  return (
    <>
      <PageHeader
        headerText="Dashboard"
        subheaderText="A dashboard shared with you"
        onBack={() => navigate(-1)}
        inlineBack
      />

      {state === "loading" && (
        <Notice title="Opening the dashboard…" body="One moment." />
      )}

      {state === "denied" && (
        <Notice
          title="This dashboard isn't shared with you"
          body="Ask whoever owns it to share it, and it will show up here."
        />
      )}

      {state === "error" && (
        <Notice
          title="Couldn't load the dashboard"
          body="Something went wrong on the way. Try again in a moment."
        />
      )}

      {state === "ready" && board && (
        <SoftBox sx={{ margin: { mini: "1rem", sm: "1.5rem" } }}>
          <div
            className="db-root"
            style={{ "--db-brand": brand, "--db-brand-soft": `${brand}1a` }}
          >
            <DashboardBuilder
              dashboard={board}
              metricsApi={metricsApi}
              readOnly
              backLabel="Back"
              onBack={() => navigate(-1)}
            />
          </div>
        </SoftBox>
      )}
    </>
  );
};

export default SharedDashboard;
